import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useShop } from '../context/ShopContext';
import { useNavigate, Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

export default function Checkout() {
  const { cart, cartTotal, setIsCheckoutLoading } = useShop();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    zip: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (step < 3) setStep(step + 1);
    else {
      setIsCheckoutLoading(true);
      setTimeout(() => {
        setIsCheckoutLoading(false);
        navigate('/success');
      }, 2000);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="pt-40 px-6 container mx-auto text-center min-h-[60vh] flex flex-col items-center justify-center">
        <h1 className="text-4xl tracking-tighter mb-8 uppercase">Bag is Empty</h1>
        <Link to="/collection" className="bg-graphite text-bone px-12 py-4 text-xs uppercase tracking-widest font-bold">Return to Collection</Link>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="pt-32 md:pt-40 px-6 container mx-auto pb-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left: Form */}
        <div className="lg:col-span-7">
          <div className="flex gap-4 mb-12 text-[10px] uppercase tracking-widest font-bold text-graphite/40">
            <span className={step >= 1 ? 'text-crimson' : ''}>01 Details</span>
            <span className="text-graphite/10">—</span>
            <span className={step >= 2 ? 'text-crimson' : ''}>02 Shipping</span>
            <span className="text-graphite/10">—</span>
            <span className={step >= 3 ? 'text-crimson' : ''}>03 Payment</span>
          </div>

          <form onSubmit={handleNext} className="space-y-12">
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h2 className="text-2xl font-bold tracking-tighter uppercase">Customer Details</h2>
                <input 
                  required
                  type="email" 
                  name="email"
                  placeholder="EMAIL ADDRESS" 
                  className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors"
                  onChange={handleInputChange}
                />
                <div className="grid grid-cols-2 gap-6">
                  <input 
                    required
                    type="text" 
                    name="firstName"
                    placeholder="FIRST NAME" 
                    className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors"
                    onChange={handleInputChange}
                  />
                  <input 
                    required
                    type="text" 
                    name="lastName"
                    placeholder="LAST NAME" 
                    className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors"
                    onChange={handleInputChange}
                  />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h2 className="text-2xl font-bold tracking-tighter uppercase">Shipping Address</h2>
                <input required name="address" placeholder="STREET ADDRESS" className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors" onChange={handleInputChange} />
                <div className="grid grid-cols-2 gap-6">
                  <input required name="city" placeholder="CITY" className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors" onChange={handleInputChange} />
                  <input required name="zip" placeholder="ZIP CODE" className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors" onChange={handleInputChange} />
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h2 className="text-2xl font-bold tracking-tighter uppercase">Payment Method</h2>
                <div className="p-8 border border-graphite/10 bg-white/50 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-4 h-4 rounded-full border-4 border-crimson" />
                    <span className="text-xs uppercase tracking-widest font-bold">Credit / Debit Card</span>
                  </div>
                  <input placeholder="CARD NUMBER" className="w-full bg-transparent border-b border-graphite/20 py-4 focus:border-crimson outline-none text-sm uppercase tracking-widest transition-colors" />
                </div>
              </motion.div>
            )}

            <div className="flex justify-between items-center pt-8">
              {step > 1 && (
                <button type="button" onClick={() => setStep(step - 1)} className="text-xs uppercase tracking-widest font-bold text-graphite/40 hover:text-graphite">Back</button>
              )}
              <button 
                type="submit" 
                className="ml-auto bg-graphite text-bone px-12 py-4 text-xs uppercase tracking-widest font-bold hover:bg-crimson transition-colors"
              >
                {step === 3 ? 'Complete Order' : 'Continue'}
              </button>
            </div>
          </form>
        </div>

        {/* Right: Summary */}
        <div className="lg:col-span-5">
          <div className="bg-white/30 p-8 border border-graphite/5 sticky top-40">
            <h3 className="text-xs uppercase tracking-widest font-bold mb-8 text-crimson">Order Summary</h3>
            <div className="space-y-6 mb-8 max-h-[40vh] overflow-y-auto no-scrollbar pr-2">
              {cart.map(item => (
                <div key={`${item.id}-${item.selectedSize}`} className="flex justify-between items-center gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-16 bg-gray-100 flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-tight">{item.name}</p>
                      <p className="text-[10px] text-graphite/40 uppercase tracking-widest">Size: {item.selectedSize} × {item.quantity}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold">${item.price * item.quantity}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-graphite/10 pt-6">
              <div className="flex justify-between text-xs uppercase tracking-widest text-graphite/40">
                <span>Shipping</span>
                <span>Calculated at next step</span>
              </div>
              <div className="flex justify-between text-lg font-bold tracking-tighter uppercase pt-4 border-t border-graphite/10 mt-4">
                <span>Total</span>
                <span>${cartTotal}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}